import { ddmmyy } from '@mds/shared/lib/format';
import type { RateCardUnit } from '@mds/shared/types/rate-card';

/**
 * The one place a stored number turns into words.
 *
 * Every derived display string the design used to keep beside its source lives
 * here instead: `$10 / day` from an amount and a unit, `Thu 17 Sep` from an
 * ISO date, `4 seats left` from a capacity and a count. Storing those beside
 * the numbers they come from is what let the design's three date fields drift
 * apart, so the data files store the source and these functions do the rest.
 */

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/**
 * `1000` → `$10`, `1250` → `$12.50`.
 *
 * Whole dollars print without the cents because every published rate is whole
 * and `$10.00 / day` reads like a machine wrote it. `maximumFractionDigits: 2`
 * still covers a rate that is not.
 */
export const formatMoney = (amountMinor: number): string => USD.format(amountMinor / 100);

/** `hour` → `/ hour`. The separate half of a price, so it can be set quieter. */
export const formatUnit = (unit: RateCardUnit): string => `/ ${unit}`;

/**
 * A package's `/ unit`, preferring its own words where it has them — the café
 * hall's `/ half-day` rather than the `/ block` its shared unit would print.
 */
export const formatPackageUnit = (pkg: { unit: RateCardUnit; unitLabel?: string }): string =>
  pkg.unitLabel ? `/ ${pkg.unitLabel}` : formatUnit(pkg.unit);

/** An event's price line: `Free` or `$15`. No unit — a ticket is per person. */
export const formatTicket = (priceMinor: number): string =>
  priceMinor === 0 ? 'Free' : formatMoney(priceMinor);

/**
 * Parse `YYYY-MM-DD` as a local date.
 *
 * `new Date('2026-09-17')` is parsed as UTC midnight, which is the day before
 * in any timezone west of Greenwich — the bug that makes an event card read
 * "16 Sep" for half the world. Building it from parts keeps it local.
 */
const fromIso = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** `2026-09-17` → `Thu 17/09/26`. The line under an event title. */
export const formatEventDate = (iso: string): string =>
  `${fromIso(iso).toLocaleDateString('en-GB', { weekday: 'short' })} ${ddmmyy(fromIso(iso))}`;

/** `2026-09-17` → `17/09/26`. The detail page's full form. */
export const formatLongDate = (iso: string): string => ddmmyy(fromIso(iso));

/** `2026-09-17` → `{ day: '17', month: 'Sep' }`. The badge on an event card. */
export const formatDayBadge = (iso: string): { day: string; month: string } => {
  const date = fromIso(iso);
  return {
    day: String(date.getDate()),
    month: date.toLocaleDateString('en-GB', { month: 'short' }),
  };
};

/** `2026-09-17` → `September 2026`. The heading the events index groups under. */
export const formatMonth = (iso: string): string =>
  fromIso(iso).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

/**
 * Group rows by the month of their `iso`, keeping the order they came in.
 * The events index renders a heading per entry and a grid of its rows.
 */
export const groupByMonth = <T extends { iso: string }>(rows: T[]): [string, T[]][] => {
  const months = new Map<string, T[]>();
  for (const row of rows) {
    const key = formatMonth(row.iso);
    const bucket = months.get(key);
    if (bucket) bucket.push(row);
    else months.set(key, [row]);
  }
  return [...months];
};

/**
 * The seats line on an event card. Sold out is stated rather than shown as a
 * zero, and the singular is worth the branch — "1 seats left" is the kind of
 * detail people notice on the one card that matters to them.
 */
export const formatSeatsLeft = (left: number): string => {
  if (left <= 0) return 'Sold out';
  if (left === 1) return '1 seat left';
  return `${left} seats left`;
};

/**
 * An event space without its room size: the API sends `Café Hall · 60 seats`,
 * and the visitor needs only the room, not how many it holds.
 */
export const formatSpaceName = (space: string): string => space.replace(/\s*·\s*\d+\s*seats?$/i, '');
